import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Stack, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ERouteLinks } from 'models/route';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { Chips } from 'components/atoms/Chips';
import { getFieldsByComponentType } from 'pages/AncillariesPage/helpers';
import { EArtAncillaryTypeName, EArtComponentType } from 'constants/ArtAccessoryType';
import { EComponentLabels } from 'pages/AncillariesPage/ComponentsPage/DetailComponents';
import {
  ComponentInformationContainer,
  ComponentInformationTitle,
} from 'pages/AncillariesPage/ComponentsPage/DetailComponents/styles';
import Link from '@mui/material/Link';
import Button from 'components/atoms/Button';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { mapAncillaryTypeToTranslationKey } from 'utils/mapAncillaryTypeToTranslation';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { IAncillaryDetailsCustomfield, IComponentDetails } from 'models/ancillaries';
import { ECustomFieldType } from 'models/customField';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { omitNullishAndEmpty } from 'utils/omitNullishAndEmpty';
import { findInDictionary, parseDictionary } from 'helpers/dictionary';
import { EMarkingTypes } from 'constants/Markings';
import { ETransactionTypes } from 'constants/TransactionTypes';

interface IComponentDetailsProps {
  id: string;
  component?: IComponentDetails;
  isForTransactionDetails?: boolean;
  initialTransaction?: boolean;
  marking?: string;
  transactionType?: ETransactionTypes;
  visibleCustomFieldsData?: boolean;
  disableActions?: boolean;
  customFieldsData?: IAncillaryDetailsCustomfield[];
}

const ComponentDetails = ({
  id,
  component,
  isForTransactionDetails = false,
  initialTransaction = false,
  marking,
  transactionType,
  visibleCustomFieldsData,
  disableActions,
  customFieldsData,
}: IComponentDetailsProps) => {
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const { hasPermissions } = usePermission();
  const navigate = useNavigate();
  const {
    loadDictionaries,
    componentDetail,
    getComponentDetail,
    state,
    firearmType,
    make,
    model,
    manufacturer,
  } = useAncillaries();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const { shortDateFormat } = useGlobalProperty();
  const [componentDetailsData, setComponentDetailsData] = useState<
    IComponentDetails | undefined
  >(undefined);
  const [showMarkings, setShowMarkings] = useState(false);

  useEffect(() => {
    if (componentDetailsData) {
      if (componentDetailsData.typeUniqueId) {
        const fields = getFieldsByComponentType(
          componentDetailsData.typeUniqueId as EArtComponentType,
        );
        setAccessFields(fields);
      } else {
        setAccessFields([]);
      }
    }
  }, [componentDetailsData]);

  useEffect(() => {
    if (!component) {
      setComponentDetailsData(componentDetail);
      return;
    }
    setComponentDetailsData({
      ...componentDetail,
      ...omitNullishAndEmpty(component),
    } as IComponentDetails);
  }, [componentDetail]);

  useEffect(() => {
    (async () => {
      if (id && hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES])) {
        await getComponentDetail(id, isForTransactionDetails);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.ArtAncillaryOptions,
        EDictionaryTypes.State,
        EDictionaryTypes.FirearmType,
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.Manufacturer,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const showField = (fieldName: string) => accessFields.includes(fieldName);

  const renderCustomFieldData = (customField: IAncillaryDetailsCustomfield) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value || '-';
  };

  // eslint-disable-next-line consistent-return
  const handleCustomFieldsData = () => {
    if (customFieldsData && customFieldsData.length) {
      return customFieldsData;
    }
    if (componentDetailsData?.customFields) {
      return componentDetailsData.customFields;
    }
  };

  return (
    <div>
      {componentDetailsData && (
        <Grid sx={{ gap: 3 }}>
          <DetailsTemplate
            title={componentDetailsData?.type}
            pageType={EDetailsPageTypes.view}
            editPermissions={[EPermission.EDIT_ANCILLARY]}
            redirectToView={() => navigate(`${ERouteLinks.ViewComponents.replace(':id', id)}`)}
            disableActions={disableActions}
            actionButtonProps={{
              ...{ label: t('legalEntityDetails.ancillary.accordion.details.link') },
              ...{ size: 'small' },
              ...{ sx: { minWidth: '207px !important' } },
              ...{ sizePX: EButtonSizesPX.px24 },
              ...{ variant: EButtonVariants.outlined },
            }}
          >
            <ComponentInformationContainer size={{ xs: 12 }} container>
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <ComponentInformationTitle variant="subtitle1">
                    {t(EComponentLabels.componentGeneralInformation)}
                  </ComponentInformationTitle>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.ancillaryType)}
                  </Typography>
                  <Typography variant="body2">
                    {t(mapAncillaryTypeToTranslationKey(componentDetailsData?.ancillaryType))}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.componentType)}
                  </Typography>
                  <Typography variant="body2">{componentDetailsData?.type}</Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                  >
                    {t(EComponentLabels.artId)}
                  </Typography>
                  <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                    {componentDetailsData?.artId}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Link
                    href={`${ERouteLinks.ViewAncillaries.replace(
                      ':id',
                      componentDetailsData.artUniqueId,
                    ).replace(':type', EArtAncillaryTypeName.COMPONENTS.toLowerCase())}`}
                    underline="none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      fullWidth
                      label={t('componentDetails.showArtInformation.button')}
                      variant={EButtonVariants.contained}
                      size={EButtonSizes.small}
                    />
                  </Link>
                </Stack>
              </Grid>

              {showField('manufacturer') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.manufacturer)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(componentDetailsData?.manufacturer)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('productName') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.productName)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(componentDetailsData?.productName)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('calibre') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.calibre)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(componentDetailsData?.calibre)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('composition') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.composition)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(componentDetailsData?.composition)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('proofHouse') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.proofHouse)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(componentDetailsData?.proofHouse)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('capacity') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.capacity)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(componentDetailsData?.capacity)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {showField('length') && (
                <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(EComponentLabels.length)}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(componentDetailsData?.length)}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </ComponentInformationContainer>
            <ComponentInformationContainer size={{ xs: 12 }} container>
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <ComponentInformationTitle variant="subtitle1">
                    {t(EComponentLabels.componentDetailedInformation)}
                  </ComponentInformationTitle>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.firecycleID)}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(componentDetailsData?.fireCycleId)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.serialNumber)}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(componentDetailsData?.serialNumber)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.componentState)}
                  </Typography>
                  <Typography variant="body2">
                    <Chips
                      text={emptyFieldHolder(
                        parseDictionary(state, 'name')[componentDetailsData.stateUniqueId],
                      )}
                      variant={
                        renderDataColorByValues(
                          componentDetailsData.activityUniqueId,
                          componentDetailsData.legalityUniqueId,
                          componentDetailsData.stateUniqueId,
                        ).stateField
                      }
                    />
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.exhibitNumber)}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(componentDetailsData?.exhibitNumber)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.currentOwner)}
                  </Typography>
                  <Typography variant="body2">
                    {!isForTransactionDetails &&
                    componentDetailsData.isOwnerHyperLinkActive ? (
                      <StyledLegalLink
                        legalId={componentDetailsData.ownerId}
                        legalType={getLegalEntityType(componentDetailsData.ownerType)}
                        labelText={componentDetailsData?.ownerName}
                        legalVisibilityUniqueId={componentDetailsData.ownerVisibilityUniqueId}
                      />
                    ) : (
                      <Typography variant="body2">
                        {emptyFieldHolder(componentDetailsData?.ownerName)}
                      </Typography>
                    )}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.currentKeeper)}
                  </Typography>
                  <Typography variant="body2">
                    {!isForTransactionDetails &&
                    componentDetailsData.isKeeperHyperLinkActive ? (
                      <StyledLegalLink
                        legalId={componentDetailsData.keeperId}
                        legalType={getLegalEntityType(componentDetailsData.keeperType)}
                        labelText={componentDetailsData?.keeperName}
                        legalVisibilityUniqueId={componentDetailsData.keeperVisibilityUniqueId}
                      />
                    ) : (
                      <Typography variant="body2">
                        {emptyFieldHolder(componentDetailsData?.keeperName)}
                      </Typography>
                    )}
                  </Typography>
                </Stack>
              </Grid>
              {isForTransactionDetails && initialTransaction && (
                <>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {transactionType === ETransactionTypes.DECLARED_IMPORTED
                          ? t(EComponentLabels.importMarking)
                          : t(EComponentLabels.appliedMarking)}
                      </Typography>
                      <Typography variant="body2">{emptyFieldHolder(marking)}</Typography>
                    </Stack>
                  </Grid>
                </>
              )}
              {visibleCustomFieldsData && (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {handleCustomFieldsData()?.map((customField) => (
                    <Grid size={{ xs: 6, lg: 3 }} key={customField.customFieldUniqueId}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {customField?.translations?.filter(
                          (translation) =>
                            selectedLanguage?.uniqueId === translation.languageUniqueId,
                        )?.[0]?.name ||
                          customField?.translations?.filter(
                            (translation) =>
                              defaultLanguage?.uniqueId === translation.languageUniqueId,
                          )?.[0]?.name}
                      </Typography>
                      <Typography variant="body2">
                        {renderCustomFieldData(customField)}
                      </Typography>
                    </Grid>
                  ))}
                </>
              )}
            </ComponentInformationContainer>
            <ComponentInformationContainer
              size={{ xs: 12 }}
              container
              borderHidden={isForTransactionDetails || !showMarkings}
            >
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <ComponentInformationTitle variant="subtitle1">
                    {t(EComponentLabels.firearmInformation)}
                  </ComponentInformationTitle>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.firearmManufacturer)}
                  </Typography>
                  <Typography variant="body2">
                    {findInDictionary(
                      componentDetailsData?.frtManufacturerUniqueId,
                      manufacturer,
                    )}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.firearmReferenceNo)}
                  </Typography>
                  <Typography variant="body2">
                    {emptyFieldHolder(componentDetailsData?.frtReferenceNo)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Stack>
                  {hasPermissions([EPermission.VIEW_FIREARMS]) && (
                    <Link
                      href={ERouteLinks.FRTDetails.replace(
                        ':frtId',
                        componentDetailsData.frtId,
                      )}
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        fullWidth
                        label={t('componentDetails.showFrtInformation.button')}
                        variant={EButtonVariants.contained}
                        size={EButtonSizes.small}
                      />
                    </Link>
                  )}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.firearmFirecycleType)}
                  </Typography>
                  <Typography variant="body2">
                    {findInDictionary(componentDetailsData?.frtTypeUniqueId, firearmType)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.firearmMake)}
                  </Typography>
                  <Typography variant="body2">
                    {findInDictionary(componentDetailsData?.frtMakeUniqueId, make)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(EComponentLabels.firearmModel)}
                  </Typography>
                  <Typography variant="body2">
                    {findInDictionary(componentDetailsData?.frtModelUniqueId, model)}
                  </Typography>
                </Stack>
              </Grid>
            </ComponentInformationContainer>
            {!isForTransactionDetails && (
              <ComponentInformationContainer size={{ xs: 12 }} container borderHidden>
                {componentDetailsData.markings && showMarkings && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Stack>
                        <ComponentInformationTitle variant="subtitle1">
                          {t(EComponentLabels.componentMarkings)}
                        </ComponentInformationTitle>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.importMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          componentDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.IMPORT,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.appliedMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          componentDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.APPLIED,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.deactivationMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          componentDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.DEACTIVATION,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.exportMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          componentDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.EXPORT,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.surplusSaleMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          componentDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.SURPLUS_SALE,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.yearOfTransfer)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(componentDetailsData.yearOfTransfer)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.confiscationMarking)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(
                          componentDetailsData.markings.find(
                            (marking) => marking.markingType === EMarkingTypes.CONFISCATION,
                          )?.marking,
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.yearOfConfiscation)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(componentDetailsData.yearOfConfiscation)}
                      </Typography>
                    </Grid>
                  </>
                )}

                {componentDetailsData.markings && (
                  <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'flex-end', mt: 3 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        fullWidth
                        label={
                          showMarkings
                            ? t('ancillaryDetails.hideAncillaryMarkings.button')
                            : t('ancillaryDetails.seeAncillaryMarkings.button')
                        }
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        onClick={() => setShowMarkings(!showMarkings)}
                      />
                    </Grid>
                  </Grid>
                )}
              </ComponentInformationContainer>
            )}
          </DetailsTemplate>
        </Grid>
      )}
    </div>
  );
};

export default ComponentDetails;
